import {useState, useMemo, useCallback} from 'react';
import {useAppSelector} from '@/store';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';

/**
 * Base hook for game review functionality
 * Provides common state management for game review screens
 */
const useGameReviewBase = (activityFormData) => {
  const countryCode = useAppSelector(selectCountryCode);

  const [gameDisplayTabIndex, setGameDisplayTabIndex] = useState(0);
  const [gameSettingsTabIndex, setGameSettingsTabIndex] = useState(0);
  const [tutorialTabIndex, setTutorialTabIndex] = useState(0);
  const [previewModal, setPreviewModal] = useState({
    show: false,
    url: '',
    type: 'image',
    title: ''
  });

  const handlePreview = useCallback((url, type, title) => {
    setPreviewModal({show: true, url, type, title});
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewModal({show: false, url: '', type: 'image', title: ''});
  }, []);

  const translations = activityFormData?.translations || {};
  const displayConfig = activityFormData?.displayConfig || {};
  const asset = displayConfig?.asset || {};

  const languages = useMemo(
    () =>
      COUNTRY_LANGUAGES_CODES[countryCode] || [
        {code: 'en', title: 'English', languageId: 1}
      ],
    [countryCode]
  );

  const languageCodes =
    Object.keys(translations).length > 0
      ? Object.keys(translations)
      : languages.map((l) => l.code);

  const gameDisplayLangCode = languageCodes[gameDisplayTabIndex] || 'en';
  const gameDisplayTranslation = translations[gameDisplayLangCode] || {};

  const gameSettingsLangCode = languageCodes[gameSettingsTabIndex] || 'en';
  const gameSettingsTranslation = translations[gameSettingsLangCode] || {};

  const tutorialLangCode = languageCodes[tutorialTabIndex] || 'en';
  const tutorialTranslation = translations[tutorialLangCode] || {};

  return {
    // Tab state
    gameDisplayTabIndex,
    setGameDisplayTabIndex,
    gameSettingsTabIndex,
    setGameSettingsTabIndex,
    tutorialTabIndex,
    setTutorialTabIndex,

    // Preview modal state
    previewModal,
    handlePreview,
    handleClosePreview,

    // Data
    translations,
    displayConfig,
    asset,
    languages,
    languageCodes,

    // Language-specific translations
    gameDisplayLangCode,
    gameDisplayTranslation,
    gameSettingsLangCode,
    gameSettingsTranslation,
    tutorialLangCode,
    tutorialTranslation
  };
};

export default useGameReviewBase;
